// Draws the arena with Bombermaaan's sprite sheets (public/gfx, GPL-3), following its
// layers: floor, items, walls, flames, bombs, then bombers and other tall things sorted by Y.
import {
  BLOCK,
  COLS,
  ROWS,
  BState,
  Dir,
  Sickness,
  type BombView,
  type ItemView,
  type PlayerView,
  type Snapshot,
} from "../shared/protocol.js";

export const PLAYER_COLORS = ["#f4f4f8", "#55556a", "#e63946", "#3d8bfd"];
export const WIDTH = COLS * BLOCK;
export const HEIGHT = ROWS * BLOCK;

interface Sheet {
  img: HTMLImageElement;
  w: number;
  h: number;
  cols: number;
}

function sheet(name: string, cols: number, w = BLOCK, h = BLOCK): Sheet {
  const img = new Image();
  img.src = `/gfx/${name}.png`;
  return { img, w, h, cols };
}

const S = {
  floor: sheet("arena_floor", 2),
  arrows: sheet("arena_arrows", 4),
  wall: sheet("arena_wall", 7),
  fly: sheet("arena_fly", 4),
  bomb: sheet("arena_bomb", 3),
  remote: sheet("arena_remote_bomb", 3),
  flame: sheet("arena_flame", 28),
  item: sheet("arena_item", 20),
  fire: sheet("arena_fire", 7, 52, 54),
  fumes: sheet("arena_fumes", 3),
  walk: sheet("arena_bomber_walk", 12, 42, 44),
  hold: sheet("arena_bomber_walk_hold", 12, 42, 44),
  lift: sheet("arena_bomber_lift", 12, 42, 44),
  throw: sheet("arena_bomber_throw", 20, 42, 44),
  punch: sheet("arena_bomber_punch", 8, 42, 44),
  stunt: sheet("arena_bomber_stunt", 4, 42, 44),
  death: sheet("arena_bomber_death", 7, 42, 44),
  hurry: sheet("arena_hurry", 1, 200, 36),
};

// Sprite direction order in the bomber sheets: down, right, left, up.
const SPRITE_DIR: Record<Dir, number> = {
  [Dir.None]: 0,
  [Dir.Down]: 0,
  [Dir.Right]: 1,
  [Dir.Left]: 2,
  [Dir.Up]: 3,
};
const ARROW_FRAME: Record<Dir, number> = { [Dir.None]: 0, [Dir.Right]: 0, [Dir.Down]: 1, [Dir.Left]: 2, [Dir.Up]: 3 };
const WALL_BURN_ENDS = [0.12, 0.23, 0.33, 0.42];
const FLAME_STATES: [number, number][] = [
  [0.06, 2],
  [0.13, 1],
  [0.21, 0],
  [0.28, 1],
  [0.34, 2],
  [0.4, 3],
];
const FIRE_ENDS = [0.063, 0.153, 0.279, 0.414, 0.504, 0.567];
const DEATH_ENDS = [0.6, 0.72, 0.78, 0.84, 0.9, 0.96];
const THROW_ENDS = [0.04, 0.08, 0.12, 0.2];
// Flame sprite bases per ray (up, down, left, right): middle segment, tip.
const RAY_SPRITES: [number, number][] = [
  [0, 8],
  [0, 16],
  [4, 12],
  [4, 20],
];
const RAY_DELTAS: [number, number][] = [
  [0, -1],
  [0, 1],
  [-1, 0],
  [1, 0],
];

const frameAt = (t: number, ends: number[]) => {
  const i = ends.findIndex((e) => t < e);
  return i < 0 ? ends.length : i;
};

type Draw = { y: number; fn: () => void };

export class Renderer {
  private g: CanvasRenderingContext2D;

  constructor(canvas: HTMLCanvasElement) {
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    this.g = canvas.getContext("2d", { alpha: false })!;
    this.g.imageSmoothingEnabled = false;
  }

  private spr(s: Sheet, index: number, x: number, y: number, g = this.g) {
    if (!s.img.complete || !s.img.naturalWidth) return;
    const col = index % s.cols;
    const row = Math.floor(index / s.cols);
    g.drawImage(s.img, 1 + col * (s.w + 1), 1 + row * (s.h + 1), s.w, s.h, Math.round(x), Math.round(y), s.w, s.h);
  }

  private floorLayer = document.createElement("canvas");
  private floorKey = "";

  private floor(s: Snapshot) {
    const loaded = S.floor.img.complete && S.arrows.img.complete;
    const key = `${loaded}|${s.grid}|${s.arrows.join(";")}`;
    if (key === this.floorKey) return;
    this.floorKey = key;
    const layer = this.floorLayer;
    layer.width = WIDTH;
    layer.height = HEIGHT;
    const g = layer.getContext("2d")!;
    g.imageSmoothingEnabled = false;
    for (let y = 0; y < ROWS; y++) {
      for (let x = 0; x < COLS; x++) {
        const above = y > 0 && s.grid.charCodeAt((y - 1) * COLS + x) !== 48;
        this.spr(S.floor, above ? 1 : 0, x * BLOCK, y * BLOCK, g);
      }
    }
    for (const [c, d] of s.arrows) this.spr(S.arrows, ARROW_FRAME[d], (c % COLS) * BLOCK, Math.floor(c / COLS) * BLOCK, g);
  }

  // `age` is how long ago the snapshot arrived, so server timers can be advanced smoothly.
  draw(s: Snapshot, players: PlayerView[], bombs: BombView[], youId: string, now: number, age: number) {
    const g = this.g;
    const t = now / 1000;
    const tile = (i: number) => s.grid.charCodeAt(i) - 48;
    const burning = new Map(s.burning);
    const falling = new Map(s.falling);

    // Floor (with a shadow under walls) and arrows only change when walls do: drawn once
    // into a cached layer.
    this.floor(s);
    g.fillStyle = "#000";
    g.fillRect(0, 0, WIDTH, HEIGHT);
    g.drawImage(this.floorLayer, 0, 0);

    // Items lying on the floor
    const tall: Draw[] = [];
    const blink = Math.floor(t / 0.08) % 2;
    for (const it of s.items) this.item(it, blink, t, age, tall);

    // Walls
    for (let i = 0; i < COLS * ROWS; i++) {
      const x = (i % COLS) * BLOCK;
      const y = Math.floor(i / COLS) * BLOCK;
      const k = tile(i);
      if (k === 1) this.spr(S.wall, 0, x, y);
      else if (k === 2) {
        const b = burning.get(i);
        this.spr(S.wall, b === undefined ? 1 : 2 + frameAt(b + age, WALL_BURN_ENDS), x, y);
      }
    }
    for (const [c, ft] of falling) {
      const x = (c % COLS) * BLOCK;
      const y = Math.floor(c / COLS) * BLOCK;
      this.spr(S.fly, 3, x, y);
      const fy = Math.min(y, y - HEIGHT + (ft + age) * 500);
      tall.push({ y: fy, fn: () => this.spr(S.fly, [0, 1, 2, 1][Math.floor(t / 0.05) % 4], x, fy) });
    }

    // Flames
    for (const e of s.explosions) {
      const et = e.t + age;
      const st = FLAME_STATES.find(([end]) => et < end)?.[1];
      if (st === undefined) continue;
      this.spr(S.flame, 24 + st, e.x * BLOCK, e.y * BLOCK);
      e.r.forEach((len, r) => {
        const [mid, tip] = RAY_SPRITES[r];
        const [dx, dy] = RAY_DELTAS[r];
        for (let i = 1; i <= len; i++) {
          const end = i === len && e.e & (1 << r);
          this.spr(S.flame, (end ? tip : mid) + st, (e.x + dx * i) * BLOCK, (e.y + dy * i) * BLOCK);
        }
      });
    }

    // Bombs on the floor
    const air: BombView[] = [];
    for (const b of bombs) {
      if (b.air) {
        air.push(b);
        continue;
      }
      this.bomb(b, t);
    }

    // Bombers, sorted by Y with the other tall things
    for (const p of players) {
      if (!p.playing || p.state === BState.Dead) continue;
      tall.push({ y: p.y, fn: () => this.bomber(p, p.id === youId, t, p.st + age) });
    }
    tall.sort((a, b) => a.y - b.y);
    for (const d of tall) d.fn();

    for (const b of air) this.bomb(b, t);

    if (s.hurry >= 0) {
      const hx = -68 + (s.hurry + age) * 308;
      if (hx < WIDTH) this.spr(S.hurry, 0, hx, 96);
    }
  }

  private item(it: ItemView, blink: number, t: number, age: number, tall: Draw[]) {
    if (it.fly) {
      const frame = [0, 1, 2, 1][Math.floor(t / 0.05) % 4];
      tall.push({ y: it.y, fn: () => this.spr(S.fly, frame, it.x, it.y) });
      return;
    }
    if (it.burn !== undefined) {
      this.spr(S.item, it.k * 2, it.x, it.y);
      const f = frameAt(it.burn + age, FIRE_ENDS);
      tall.push({ y: it.y, fn: () => this.spr(S.fire, f, it.x - 10, it.y - 22) });
      return;
    }
    this.spr(S.item, it.k * 2 + blink, it.x, it.y);
    if (it.fumes !== undefined) {
      const f = Math.min(2, Math.floor((it.fumes + age) / 0.1));
      const quads: [number, number][] = [
        [-16, -16],
        [16, -16],
        [16, 16],
        [-16, 16],
      ];
      tall.push({ y: it.y, fn: () => quads.forEach(([dx, dy], q) => this.spr(S.fumes, f + 3 * q, it.x + dx, it.y + dy)) });
    }
  }

  private bomb(b: BombView, t: number) {
    const step = b.fuse > 2.05 && b.fuse < 90 ? 0.3 : 0.2;
    const frame = [2, 1, 0, 1][Math.floor(t / step) % 4];
    this.spr(b.remote ? S.remote : S.bomb, frame, b.x, b.y - b.z);
  }

  private bomber(p: PlayerView, isYou: boolean, t: number, st: number) {
    const x = p.x - 6;
    const y = p.y - 12;
    const dir = SPRITE_DIR[p.dir];
    if (p.state === BState.Dying) {
      if (st < 1.02) this.spr(S.death, p.color * 7 + frameAt(st, DEATH_ENDS), x, y);
      return;
    }
    let sh = S.walk;
    let col = dir * 3 + (p.moving ? [1, 0, 1, 2][Math.floor(t / 0.15) % 4] : 1);
    switch (p.state) {
      case BState.Hold:
        sh = S.hold;
        break;
      case BState.Lift:
        sh = S.lift;
        col = dir * 3 + Math.min(2, Math.floor(st / 0.06));
        break;
      case BState.Throw:
        sh = S.throw;
        col = dir * 5 + frameAt(st, THROW_ENDS);
        break;
      case BState.Punch:
        sh = S.punch;
        col = dir * 2 + (st >= 0.08 && st < 0.22 ? 1 : 0);
        break;
      case BState.Stunned:
        sh = S.stunt;
        col = (dir + Math.floor(st / 0.1)) % 4;
        break;
    }
    let row = p.color;
    if (p.sick !== Sickness.None && Math.floor(t / 0.09) % 2 === 0) {
      row = p.sick === Sickness.Invisibility ? 6 : 5;
    } else if (p.sick === Sickness.Invisibility) {
      row = -1;
    }
    if (row >= 0) this.spr(sh, row * sh.cols + col, x, y);
    if (p.shield > 0 && (p.shield > 2 || Math.floor(p.shield * 10) % 2 === 0)) this.spr(sh, 7 * sh.cols + col, x, y);
    if (p.sick === Sickness.Flameproof) this.spr(sh, 7 * sh.cols + col, x, y);

    if (isYou) {
      const g = this.g;
      const ay = y - 6 + Math.sin(t * 5) * 2;
      g.fillStyle = PLAYER_COLORS[p.color];
      g.strokeStyle = "#000";
      g.lineWidth = 1;
      g.beginPath();
      g.moveTo(p.x + 11, ay - 5);
      g.lineTo(p.x + 21, ay - 5);
      g.lineTo(p.x + 16, ay + 1);
      g.closePath();
      g.fill();
      g.stroke();
    }
  }
}
